import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import moment from 'moment';
import { getDentalSlotBlockAudit } from '../../../services/api/cashless-dentals';
import LoadingComponent from '../../common/LoadingComponent';

const PAGE_LIMIT = 20;

const emptyPagination = {
    page: 1,
    limit: PAGE_LIMIT,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
    showing: '',
};

const SpocActivityModal = ({ isOpen, onClose }) => {

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [rows, setRows] = useState([]);
    const [pagination, setPagination] = useState(emptyPagination);
    const [page, setPage] = useState(1);

    // Server-side filters (sent as request params).
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [partnerFilter, setPartnerFilter] = useState('');
    // Server-side filters: action maps to block/unblock, address is a
    // case-insensitive substring match. Both filter in SQL, so pagination.total
    // stays accurate for the filtered set.
    const [actionFilter, setActionFilter] = useState('');
    // addressInput is what the text field shows; addressFilter is the debounced
    // value actually sent to the backend (avoids a request on every keystroke).
    const [addressInput, setAddressInput] = useState('');
    const [addressFilter, setAddressFilter] = useState('');
    // Client-side filter (backend has no reason param) — scoped to the current page.
    const [reasonFilter, setReasonFilter] = useState('');

    // Reset everything when the modal closes.
    useEffect(() => {
        if (!isOpen) {
            setRows([]);
            setError(null);
            setLoading(false);
            setPagination(emptyPagination);
            setPage(1);
            setStartDate('');
            setEndDate('');
            setPartnerFilter('');
            setActionFilter('');
            setAddressInput('');
            setAddressFilter('');
            setReasonFilter('');
        }
    }, [isOpen]);

    // Single fetch effect: runs on open and whenever page or a server-side
    // filter changes. Reason is client-side, so it is not a dependency here.
    useEffect(() => {
        if (!isOpen) {
            return;
        }

        let cancelled = false;
        setLoading(true);
        setError(null);

        const params = { page, limit: PAGE_LIMIT };
        if (startDate) {
            params.startDate = startDate;
        }
        if (endDate) {
            params.endDate = endDate;
        }
        if (partnerFilter) {
            params.partnerId = partnerFilter;
        }
        if (actionFilter) {
            params.action = actionFilter;
        }
        const trimmedAddress = addressFilter.trim();
        if (trimmedAddress) {
            params.address = trimmedAddress;
        }

        getDentalSlotBlockAudit(params)
            .then((response) => {
                if (cancelled) {
                    return;
                }
                // baseRequestBackendTs already unwrapped response.data.data, so
                // `response` here is { data: [...items], pagination: {...} }.
                const items = response && Array.isArray(response.data) ? response.data : [];
                const meta = (response && response.pagination) || {};
                setRows(items);
                setPagination({ ...emptyPagination, ...meta });
                setLoading(false);
            })
            .catch((err) => {
                if (cancelled) {
                    return;
                }
                setError((err && err.message) || 'Request failed');
                setRows([]);
                setPagination(emptyPagination);
                setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [isOpen, page, startDate, endDate, partnerFilter, actionFilter, addressFilter]);

    // Debounce the address text input: commit to the backend-bound filter 400ms
    // after the user stops typing, and reset to page 1 on change.
    useEffect(() => {
        if (addressInput === addressFilter) {
            return undefined;
        }
        const handle = setTimeout(() => {
            setAddressFilter(addressInput);
            setPage(1);
        }, 400);
        return () => clearTimeout(handle);
    }, [addressInput, addressFilter]);

    // Changing a server-side filter resets to page 1. When already on page 1,
    // the page value does not change, so the fetch effect (which also depends on
    // the filters) re-runs on its own — no double fetch.
    const resetToFirstPage = () => setPage(1);

    const onStartDateChange = (value) => {
        setStartDate(value);
        resetToFirstPage();
    };
    const onEndDateChange = (value) => {
        setEndDate(value);
        resetToFirstPage();
    };
    const onPartnerChange = (value) => {
        setPartnerFilter(value);
        resetToFirstPage();
    };
    const onActionChange = (value) => {
        setActionFilter(value);
        resetToFirstPage();
    };

    const actionLabel = (action) => {
        if (action === 'block') {
            return 'Slot Blocked';
        }
        if (action === 'unblock') {
            return 'Slot Unblocked';
        }
        return action || '—';
    };

    const blockTypeLabel = (blockType) => {
        if (blockType === 'full_day') {
            return 'Full Day';
        }
        if (blockType === 'specific_slots') {
            return 'Specific Slots';
        }
        return blockType || '—';
    };

    const fmtDates = (dates) => {
        if (!Array.isArray(dates) || !dates.length) {
            return '—';
        }
        return dates
            .map((d) => (moment(d).isValid() ? moment(d).format('DD MMM YYYY') : d))
            .join(', ');
    };

    const reasonLabel = (reason, reasonText) => {
        const base = reason ? String(reason).replace(/_/g, ' ') : '';
        if (reasonText) {
            return base ? `${base} — ${reasonText}` : reasonText;
        }
        return base || '—';
    };

    const fmtDateTime = (ts) => {
        if (!ts || !moment(ts).isValid()) {
            return '—';
        }
        // Backend sends the timestamp already in IST — parse as-is and format.
        // Do NOT call .utc()/.utcOffset() here or the time double-shifts +5:30.
        return moment(ts).format('DD MMM YYYY, hh:mm A');
    };

    // null/absent partner means an MDP request, matching the main requests view.
    const partnerLabel = (partnerName) => (partnerName ? partnerName : 'MDP');

    // Partner dropdown options: build { id, label } from the current page's rows.
    // (No dedicated partner-list endpoint; distinct partners seen in data.)
    const partnerOptions = useMemo(() => {
        const map = new Map();
        rows.forEach((row) => {
            if (row && row.partnerId != null && row.partnerName) {
                map.set(String(row.partnerId), row.partnerName);
            }
        });
        return Array.from(map.entries())
            .map(([id, label]) => ({ id, label }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [rows]);

    // Distinct reason values on the current page, for the client-side reason dropdown.
    const reasonOptions = useMemo(() => {
        const set = new Set();
        rows.forEach((row) => {
            if (row && row.reason) {
                set.add(row.reason);
            }
        });
        return Array.from(set).sort();
    }, [rows]);

    // Reason filters the already-fetched page in place.
    const visibleRows = useMemo(() => {
        if (!reasonFilter) {
            return rows;
        }
        return rows.filter((row) => row.reason === reasonFilter);
    }, [rows, reasonFilter]);

    const hasAnyFilter = startDate || endDate || partnerFilter || actionFilter || addressInput || reasonFilter;

    const clearFilters = () => {
        setReasonFilter('');
        setStartDate('');
        setEndDate('');
        setPartnerFilter('');
        setActionFilter('');
        setAddressInput('');
        setAddressFilter('');
        setPage(1);
    };

    const goPrev = () => {
        if (pagination.hasPrevPage && page > 1) {
            setPage((p) => p - 1);
        }
    };
    const goNext = () => {
        if (pagination.hasNextPage) {
            setPage((p) => p + 1);
        }
    };

    // Each filter control grows to fill the row and wraps to the next line on
    // narrow screens (min 140px wide before wrapping).
    const filterCellStyle = { flex: '1 1 140px', minWidth: 0 };

    return <Modal isOpen={isOpen} toggle={onClose} size='xl' style={{ maxWidth: '95vw', width: 'min(1200px, 95vw)' }}>
        <ModalHeader toggle={onClose}>
            SPOC Slot Activity
        </ModalHeader>
        <ModalBody>
            <div className='d-flex flex-wrap align-items-end mb-3' style={{ gap: '0.75rem' }}>
                <div style={filterCellStyle}>
                    <label className='small text-muted mb-1 d-block'>From Date</label>
                    <input
                        type='date'
                        className='form-control form-control-sm'
                        value={startDate}
                        max={endDate || undefined}
                        onChange={(e) => onStartDateChange(e.target.value)}
                    />
                </div>
                <div style={filterCellStyle}>
                    <label className='small text-muted mb-1 d-block'>To Date</label>
                    <input
                        type='date'
                        className='form-control form-control-sm'
                        value={endDate}
                        min={startDate || undefined}
                        onChange={(e) => onEndDateChange(e.target.value)}
                    />
                </div>
                <div style={filterCellStyle}>
                    <label className='small text-muted mb-1 d-block'>Partner</label>
                    <select
                        className='form-control form-control-sm'
                        value={partnerFilter}
                        onChange={(e) => onPartnerChange(e.target.value)}
                    >
                        <option value=''>All</option>
                        {partnerOptions.map((p) => (
                            <option key={p.id} value={p.id}>
                                {p.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div style={filterCellStyle}>
                    <label className='small text-muted mb-1 d-block'>Action</label>
                    <select
                        className='form-control form-control-sm'
                        value={actionFilter}
                        onChange={(e) => onActionChange(e.target.value)}
                    >
                        <option value=''>All</option>
                        <option value='block'>Slot Blocked</option>
                        <option value='unblock'>Slot Unblocked</option>
                    </select>
                </div>
                <div style={filterCellStyle}>
                    <label className='small text-muted mb-1 d-block'>Address</label>
                    <input
                        type='text'
                        className='form-control form-control-sm'
                        placeholder='e.g. noida'
                        value={addressInput}
                        onChange={(e) => setAddressInput(e.target.value)}
                    />
                </div>
                <div style={filterCellStyle}>
                    <label className='small text-muted mb-1 d-block'>Reason (this page)</label>
                    <select
                        className='form-control form-control-sm'
                        value={reasonFilter}
                        onChange={(e) => setReasonFilter(e.target.value)}
                    >
                        <option value=''>All</option>
                        {reasonOptions.map((r) => (
                            <option key={r} value={r}>
                                {String(r).replace(/_/g, ' ')}
                            </option>
                        ))}
                    </select>
                </div>
                {hasAnyFilter && (
                    <div>
                        <Button
                            size='sm'
                            color='link'
                            className='px-0'
                            onClick={clearFilters}
                        >
                            Clear filters
                        </Button>
                    </div>
                )}
            </div>

            {loading ? (
                <LoadingComponent color={'black'} />
            ) : error ? (
                <div className='text-danger text-center'>
                    Could not load SPOC activity. Please try again.
                    <div className='text-muted small mt-1'>{error}</div>
                </div>
            ) : !rows.length ? (
                <div className='text-muted text-center'>
                    No slot activity found.
                </div>
            ) : (
                <div style={{ maxHeight: '55vh', overflow: 'auto' }}>
                    <table className='table table-sm table-striped mb-0' style={{ minWidth: '1050px' }}>
                        <thead>
                            <tr>
                                <th style={{ minWidth: 110 }}>Action</th>
                                <th style={{ minWidth: 90 }}>Partner</th>
                                <th style={{ minWidth: 130 }}>Centre</th>
                                <th style={{ minWidth: 180 }}>Address</th>
                                <th style={{ minWidth: 110 }}>SPOC Name</th>
                                <th style={{ minWidth: 90 }}>Block Type</th>
                                <th style={{ minWidth: 120 }}>Blocked Date(s)</th>
                                <th style={{ minWidth: 140 }}>Reason</th>
                                <th style={{ minWidth: 130, whiteSpace: 'nowrap' }}>Date &amp; Time</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibleRows.length ? visibleRows.map((row, index) => (
                                <tr key={row.id != null ? row.id : index}>
                                    <td>{actionLabel(row.action)}</td>
                                    <td>{partnerLabel(row.partnerName)}</td>
                                    <td>
                                        {row.centerName || '—'}
                                        {row.centerId != null && (
                                            <div className='text-muted small'>ID: {row.centerId}</div>
                                        )}
                                    </td>
                                    <td>{row.centerAddress || '—'}</td>
                                    <td>{row.spocName || '—'}</td>
                                    <td>{blockTypeLabel(row.blockType)}</td>
                                    <td>{fmtDates(row.dates)}</td>
                                    <td>{reasonLabel(row.reason, row.reasonText)}</td>
                                    <td style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(row.createdAt)}</td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={9} className='text-center text-muted'>
                                        No activity matches the selected filters on this page.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </ModalBody>
        <ModalFooter className='d-flex flex-wrap justify-content-between align-items-center' style={{ gap: '0.5rem' }}>
            <span className='text-muted small'>
                {pagination.showing || (pagination.total ? `${pagination.total} records` : '')}
            </span>
            <div className='d-flex flex-wrap align-items-center' style={{ gap: '0.5rem' }}>
                <Button
                    size='sm'
                    color='light'
                    disabled={loading || !pagination.hasPrevPage}
                    onClick={goPrev}
                >
                    Previous
                </Button>
                <span className='small text-muted'>
                    Page {pagination.page || 1}{pagination.totalPages ? ` of ${pagination.totalPages}` : ''}
                </span>
                <Button
                    size='sm'
                    color='light'
                    disabled={loading || !pagination.hasNextPage}
                    onClick={goNext}
                >
                    Next
                </Button>
                <Button color='secondary' size='sm' onClick={onClose}>Close</Button>
            </div>
        </ModalFooter>
    </Modal>;
};

export default SpocActivityModal;
